/**
 * KAN-743: раздел "Каталог" интернет-магазина (только просмотр).
 * Слева дерево категорий, справа поиск, фильтр наличия, таблица товаров и
 * пагинация. Категория, поиск и фильтр лежат в URL. На мобильном дерево
 * сверху. Организация не в режиме интернет-магазина получает 404 от API.
 * KAN-754: владелец добавляет товары и категории вручную; панель открытой
 * ручной категории даёт переименовать и удалить её.
 */
import { useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/native-select';
import { PageLayout } from '@/components/layout/PageLayout';
import { PaginationBar } from '@/components/common/PaginationBar';
import { CategoryTree } from '@/components/catalog/CategoryTree';
import { CatalogProductsTable } from '@/components/catalog/CatalogProductsTable';
import { CatalogOwnerActions } from '@/components/catalog/CatalogOwnerActions';
import { CategoryActions } from '@/components/catalog/CategoryActions';
import { useIsOwner } from '@/hooks/useCurrentUser';
import { usePageSize } from '@/hooks/usePageSize';
import { useCatalogFilters } from '@/hooks/useCatalogFilters';
import { isNotFoundError, useCatalogCategories, useCatalogProducts } from '@/hooks/useCatalog';
import { ancestorIds, buildCategoryTree, formatFeedDate } from '@/lib/catalogTree';
import type { CatalogAvailability } from '@/types/catalog';

const AVAILABILITY_OPTIONS: Array<{ value: CatalogAvailability; label: string }> = [
  { value: 'all', label: 'Все' },
  { value: 'true', label: 'В наличии' },
  { value: 'false', label: 'Нет в наличии' },
];

function FeedStatus({ updatedAt }: { updatedAt: string | null | undefined }) {
  const date = formatFeedDate(updatedAt);
  if (date) return <span>Обновлено из фида: {date}</span>;
  return (
    <span>
      Фид ещё не загружен. Добавьте источник с типом Фид в{' '}
      <Link to="/data-sources" className="text-blue-600 hover:underline">
        Источниках данных
      </Link>
      .
    </span>
  );
}

export function CatalogPage(): JSX.Element {
  const location = useLocation();
  const filters = useCatalogFilters();
  const { category, search, availability } = filters;
  const { page, setPage, pageSize, setPageSize } = usePageSize('catalog');
  const isOwner = useIsOwner();

  useEffect(() => {
    document.title = 'Каталог - SMETA AI';
  }, []);

  // Новый фильтр: с первой страницы.
  useEffect(() => {
    setPage(1);
  }, [category, search, availability, setPage]);

  const categoriesQuery = useCatalogCategories();
  const categories = useMemo(() => categoriesQuery.data?.categories ?? [], [categoriesQuery.data]);
  const tree = useMemo(() => buildCategoryTree(categories), [categories]);
  const selectedCategory = useMemo(
    () => (category ? categories.find((c) => String(c.id) === category) : undefined),
    [categories, category]
  );
  const revealIds = useMemo(
    () => (category ? ancestorIds(categories, category) : []),
    [categories, category]
  );

  const notEcommerce = isNotFoundError(categoriesQuery.error);
  const productsQuery = useCatalogProducts({
    category: category ?? undefined,
    include_children: category ? 1 : undefined,
    search: search || undefined,
    available: availability === 'all' ? undefined : availability,
    page,
    page_size: pageSize,
  });
  const products = productsQuery.data?.results ?? [];
  const count = productsQuery.data?.count ?? 0;

  if (notEcommerce) {
    return (
      <PageLayout title="Каталог" breadcrumbs={[{ label: 'Каталог' }]} data-testid="catalog-page">
        <div className="rounded-lg border bg-white p-6 text-sm text-gray-600">
          Каталог доступен только в режиме интернет-магазина.
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Каталог"
      breadcrumbs={[{ label: 'Каталог' }]}
      subtitle={categoriesQuery.data ? <FeedStatus updatedAt={categoriesQuery.data.feed_updated_at} /> : undefined}
      actions={isOwner ? <CatalogOwnerActions categoryId={category} /> : undefined}
      data-testid="catalog-page"
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-start">
        <aside className="w-full shrink-0 rounded-lg border bg-white p-2 md:sticky md:top-40 md:w-72">
          <div className="max-h-64 overflow-y-auto md:max-h-[calc(100vh-12rem)]">
            {categoriesQuery.isLoading ? (
              <p className="p-2 text-sm text-gray-500">Загрузка категорий...</p>
            ) : categoriesQuery.isError ? (
              <p className="p-2 text-sm text-red-600">Не удалось загрузить категории</p>
            ) : (
              <CategoryTree
                tree={tree}
                totalProducts={categoriesQuery.data?.total_products ?? 0}
                selectedId={category}
                revealIds={revealIds}
                onSelect={filters.setCategory}
              />
            )}
          </div>
        </aside>

        <section className="min-w-0 flex-1 space-y-3">
          {selectedCategory && (
            <CategoryActions
              category={selectedCategory}
              canEdit={isOwner}
              onDeleted={() => filters.setCategory(null)}
            />
          )}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                type="search"
                aria-label="Поиск товаров"
                placeholder="Название или артикул"
                value={filters.searchInput}
                onChange={(e) => filters.setSearchInput(e.target.value)}
                className="pl-9"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <span className="shrink-0">Наличие</span>
              <NativeSelect
                aria-label="Наличие"
                className="w-full sm:w-44"
                options={AVAILABILITY_OPTIONS}
                value={availability}
                onChange={(e) => filters.setAvailability(e.target.value as CatalogAvailability)}
              />
            </label>
          </div>

          {productsQuery.isLoading ? (
            <p className="rounded-lg border bg-white p-6 text-sm text-gray-500">Загрузка товаров...</p>
          ) : productsQuery.isError ? (
            <p className="rounded-lg border bg-white p-6 text-sm text-red-600">Не удалось загрузить товары</p>
          ) : products.length === 0 ? (
            <p className="rounded-lg border bg-white p-6 text-center text-sm text-gray-500">Товары не найдены</p>
          ) : (
            <CatalogProductsTable products={products} backTo={`${location.pathname}${location.search}`} />
          )}

          <PaginationBar
            page={page}
            pageSize={pageSize}
            count={count}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            hasNext={productsQuery.data ? Boolean(productsQuery.data.next) : undefined}
            hasPrevious={productsQuery.data ? Boolean(productsQuery.data.previous) : undefined}
            className="px-0"
          />
        </section>
      </div>
    </PageLayout>
  );
}
